import { QueryConfig, useQuery } from "react-query";
import { useDebounce } from "../../../hooks/useDebounce";
import { getNotifySMSNotificationInfo, getNotifyVoiceNotificationInfo } from "../../../queries/notify";
import { isNil } from "lodash";
import { useNotifySettings } from "./useNotifySettings";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";

type SMSNotificationInfo = Awaited<ReturnType<ReturnType<typeof getNotifySMSNotificationInfo>>>;
type VoiceNotificationInfo = Awaited<ReturnType<ReturnType<typeof getNotifyVoiceNotificationInfo>>>;

export function useNotificationInfo(
  {
    voiceContent,
    voiceForceSendAll,
    channels,
  }: {
    voiceContent?: string | null;
    voiceForceSendAll: boolean;
    channels: Record<string, boolean>;
  },
  {
    smsConfig,
    voiceConfig,
  }: { smsConfig?: QueryConfig<SMSNotificationInfo, any>; voiceConfig?: QueryConfig<VoiceNotificationInfo, any> } = {},
) {
  const networkHeaders = useNetworkHeaders();
  const debouncedVoiceContent = useDebounce(voiceContent, 500);

  const { notifySettings, isLoading: isNotifySettingsLoading, isConfigured } = useNotifySettings();
  const { data: smsNotificationInfo, isLoading: isSMSNotificationInfoLoading } = useQuery(
    ["notifySMSNotificationInfo"],
    getNotifySMSNotificationInfo(networkHeaders),
    { keepPreviousData: true, enabled: isConfigured, ...smsConfig },
  );
  const { data: voiceNotificationInfo, isLoading: isVoiceNotificationInfoLoading } = useQuery(
    [
      "notifyVoiceNotificationInfo",
      { forceVoice: voiceForceSendAll ? "true" : "false", characterCount: debouncedVoiceContent?.length ?? 0 },
    ],
    getNotifyVoiceNotificationInfo(networkHeaders),
    { queryFnParamsFilter: (key) => [key[1]], keepPreviousData: true, enabled: isConfigured, ...voiceConfig },
  );

  const smsSendListCount = smsNotificationInfo?.smsSendListCount;
  const smsSendTotalCredits = smsNotificationInfo?.totalCredits;
  const voiceSendListCount = voiceNotificationInfo?.voiceSendListCount;
  const voiceSendTotalCredits = voiceNotificationInfo?.totalCredits;
  const availableCredits = notifySettings?.availableCredits;

  const getTotalCost = () => {
    if (isNil(smsSendTotalCredits) || isNil(voiceSendTotalCredits)) return null;
    let totalCost = 0;
    if (channels.sms) totalCost += smsSendTotalCredits;
    if (channels.voice) totalCost += voiceSendTotalCredits;
    return totalCost;
  };

  const getBalance = () => {
    const totalCreditCost = getTotalCost();
    if (isNil(availableCredits) || isNil(totalCreditCost)) return null;
    return availableCredits - totalCreditCost;
  };

  return {
    sms: {
      sendListCount: smsSendListCount,
      totalCredits: smsSendTotalCredits,
      isLoading: isSMSNotificationInfoLoading,
    },
    voice: {
      sendListCount: voiceSendListCount,
      totalCredits: voiceSendTotalCredits,
      isLoading: isVoiceNotificationInfoLoading || voiceContent !== debouncedVoiceContent,
    },
    billing: {
      available: availableCredits,
      cost: getTotalCost(),
      balance: getBalance(),
      isLoading: isNotifySettingsLoading,
      willOverdraft: getBalance() !== null && getBalance()! < 0,
    },
  };
}
